import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Theme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InteractionType } from '@loyalty/shared-common/profile-api-client';
import { Language } from '@loyalty/shared-common';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../shared/layout/Title';
import { compileDetailTemplatePreview } from '../../detail-template-preview';
import {
  isBlankSampleJson,
  parseContentExampleJson,
  sampleJsonForEditor,
} from '../../content-example';

export interface DetailTemplateModalProps {
  open: boolean;
  type: InteractionType;
  template: string;
  contentExample: Record<string, unknown> | null;
  readOnly?: boolean;
  onClose: () => void;
  onApply: (template: string, contentExample: Record<string, unknown> | null) => void;
}

const useStyles = makeStyles<Theme>(() => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    padding: 0,
  },
  splitRow: {
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
  },
  leftPane: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 10,
    minHeight: 0,
  },
  rightPane: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  rightTop: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 50%',
    minHeight: 0,
    marginBottom: 10,
  },
  rightBottom: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 50%',
    minHeight: 0,
  },
  code: {
    flexGrow: 1,
    minHeight: 0,
    height: '100%',
  },
  previewBox: {
    flexGrow: 1,
    minHeight: 0,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: 12,
    backgroundColor: '#fafafa',
    textAlign: 'left',
  },
  sectionTitle: {
    textAlign: 'left',
    margin: '0 0 8px',
  },
}));

export function DetailTemplateModal({
  open,
  type,
  template: initialTemplate,
  contentExample,
  readOnly = false,
  onClose,
  onApply,
}: DetailTemplateModalProps): React.ReactElement {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [template, setTemplate] = useState(initialTemplate);
  const [sampleJson, setSampleJson] = useState(() => sampleJsonForEditor(type, contentExample));
  const [isDirty, setIsDirty] = useState(false);
  const sampleIsBlank = isBlankSampleJson(sampleJson);
  const parsedSample = parseContentExampleJson(sampleJson);
  const canApplySample = sampleIsBlank || parsedSample !== null;

  useEffect(() => {
    if (open) {
      setTemplate(initialTemplate);
      setSampleJson(sampleJsonForEditor(type, contentExample));
      setIsDirty(false);
    }
  }, [open, initialTemplate, type, contentExample]);

  const previewSampleJson = sampleIsBlank ? sampleJsonForEditor(type, null) : sampleJson;
  const preview = useMemo(
    () => compileDetailTemplatePreview(template, previewSampleJson, i18n.language as Language),
    [template, previewSampleJson, i18n.language],
  );

  const handleApply = () => {
    if (sampleIsBlank) {
      onApply(template, null);
      onClose();
      return;
    }
    if (!parsedSample) {
      return;
    }
    onApply(template, parsedSample);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: 'auto',
          margin: 0,
          borderRadius: 0,
          padding: '0 40px',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          zIndex: 2,
          position: 'absolute',
          right: 5,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogTitle>
        <Title>{t('interactions.template-modal.title')}</Title>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.splitRow}>
          <Box className={classes.leftPane}>
            <h3 className={classes.sectionTitle}>
              {t('interactions.template-modal.template.title')}
            </h3>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <QlfCodeArea
                id="interaction-detail-template-modal-editor"
                className={classes.code}
                value={template}
                defaultLanguage="html"
                allowFullScreen
                height="100%"
                options={{ readOnly }}
                onChange={value => {
                  setTemplate(value || '');
                  setIsDirty(true);
                }}
              />
            </Box>
          </Box>
          <Box className={classes.rightPane}>
            <Box className={classes.rightTop}>
              <h3 className={classes.sectionTitle}>
                {t('interactions.template-modal.sample.title')}
              </h3>
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <QlfCodeArea
                  id="interaction-detail-sample-editor"
                  className={classes.code}
                  value={sampleJson}
                  defaultLanguage="json"
                  allowFullScreen
                  height="100%"
                  options={{ readOnly }}
                  onChange={value => {
                    setSampleJson(value || '');
                    setIsDirty(true);
                  }}
                />
              </Box>
            </Box>
            <Box className={classes.rightBottom}>
              <h3 className={classes.sectionTitle}>
                {t('interactions.template-modal.preview.title')}
              </h3>
              <Box className={classes.previewBox}>
                {!preview.ok ? (
                  <Alert severity="error">
                    {preview.error.type === 'invalid-json'
                      ? t('interactions.template-modal.errors.invalid-json', {
                          message: preview.error.message,
                        })
                      : t('interactions.template-modal.errors.handlebars', {
                          message: preview.error.message,
                        })}
                  </Alert>
                ) : preview.html ? (
                  <div dangerouslySetInnerHTML={{ __html: preview.html }} />
                ) : (
                  <p>{t('interactions.template-modal.preview.empty')}</p>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button type="button" color="grey" onClick={onClose}>
          {t('generic.actions.cancel')}
        </Button>
        <Button
          color="success"
          disabled={readOnly || !isDirty || !canApplySample}
          onClick={handleApply}
        >
          {t('generic.actions.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
